import { LabeledStatus, TabsNavigation } from 'components/molecules';
import TopBar from 'components/organisms/top-bar/TopBar';
import { TopbarTabs } from 'components/templates';
import { Form, FormikHelpers, FormikProvider, useFormik } from 'formik';
import { useTranslation } from 'react-i18next';

import { IContractResponse } from 'services/contract/types';
import * as Yup from 'yup';
import ContractFormTab from './tabs/ContractFormTab';
import ContractProductsTab from './tabs/ContractProductsTab';
import ContractTracksTab from './tabs/ContractTracksTab';
import ContractVideosTab from './tabs/ContractVideosTab';

export enum ContractTabs {
  form = 0,
  products = 1,
  tracks = 2,
  videos = 3,
}

interface IContractFormProps {
  initialValues: IContractResponse;
  onSubmit: (values: IContractResponse, formikHelpers: FormikHelpers<IContractResponse>) => void;
  backPath?: string;
  customBackItem?: React.ReactNode;
  title?: string;
  topbarContent?: React.ReactNode;
  withTabs?: boolean;
  disabledTabs?: ContractTabs[];
  displayMode?: boolean;
}

const contractValidationSchema = Yup.object().shape({
  title: Yup.string().required('Title is required'),
  labelName: Yup.string().required('Label name is required'),
  startDate: Yup.string().required('Start date is required'),
  endDate: Yup.string().nullable(),
  status: Yup.number().required(),
});

export const ContractForm = ({
  initialValues,
  onSubmit,
  backPath,
  customBackItem,
  title,
  topbarContent,
  withTabs = false,
  disabledTabs,
  displayMode = true,
}: IContractFormProps) => {
  const { t } = useTranslation();

  const formik = useFormik<IContractResponse>({
    initialValues,
    onSubmit,
    enableReinitialize: true,
    validationSchema: contractValidationSchema,
  });

  const { values, setFieldValue, handleSubmit } = formik;

  return (
    <FormikProvider value={formik}>
      <Form onSubmit={handleSubmit} className="w-full h-full">
        <TopbarTabs>
          <TopBar
            title={title}
            path={backPath ?? 'contracts'}
            customBackItem={customBackItem}
            topbarContent={
              <div className="flex w-full items-center justify-end gap-4">
                <div className="flex items-center gap-2 w-full justify-end">
                  <LabeledStatus.Contract status={values.status} />
                </div>
                <div>{topbarContent}</div>
              </div>
            }
          >
            {!!withTabs && (
              <TabsNavigation
                displayMode={displayMode}
                tabs={[
                  { value: ContractTabs.form, name: 'form' },
                  { value: ContractTabs.products, name: 'products' },
                  { value: ContractTabs.tracks, name: 'tracks' },
                  { value: ContractTabs.videos, name: 'videos' },
                ]}
                disabled={disabledTabs}
              />
            )}
          </TopBar>
        </TopbarTabs>
      </Form>
    </FormikProvider>
  );
};

ContractForm.getRouterTabs = (disabledChildren?: string[]) =>
  [
    { path: 'form', element: <ContractFormTab /> },
    { path: 'products', element: <ContractProductsTab /> },
    { path: 'tracks', element: <ContractTracksTab /> },
    { path: 'videos', element: <ContractVideosTab /> },
  ].filter((children) => !disabledChildren?.includes(children.path));

export default ContractForm;
